import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { accountsAPI } from '../services/api';
import { getDefaultAuthenticatedPath } from '../config/navigation';
import { formatIndianCurrencyWords } from '../utils/numberWords';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { ArrowLeft, Download, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '../context/LocalizationContext';
import { getSelectedAccountContext, saveSelectedAccountContext } from '../utils/accountNavigation';
import { generateStatementPdf } from '../utils/statementPdf';

const DEFAULT_TRANSACTIONS_PER_PAGE = 10;

const normalizeDetailLabel = (label) =>
  String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const isCurrentBalanceDetailRow = (row) =>
  normalizeDetailLabel(row?.key) === 'currentbalance';

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const normalizeTransactions = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((txn, index) => ({
    recordId: txn.recordId || txn.id || index + 1,
    creditAmount: toNumber(
      txn.creditAmount || txn.credit || txn.cr_amount || txn.crAmount || txn.CrAmount || txn.CR_AMOUNT
    ),
    debitAmount: toNumber(
      txn.debitAmount || txn.debit || txn.dr_amount || txn.drAmount || txn.DrAmount || txn.DR_AMOUNT
    ),
    balance: toNumber(
      txn.balance ||
        txn.closingBalance ||
        txn.runningBalance ||
        txn.closing_amount ||
        txn.ClosingAmount ||
        txn.CLOSING_AMOUNT
    ),
    transactionDate:
      txn.transactionDate ||
      txn.trxnDate ||
      txn.date ||
      txn.txnDate ||
      txn.TransactionDate ||
      txn.DATE ||
      '-',
    remarks:
      txn.remarks || txn.narration || txn.description || txn.Remarks || txn.Narration || 'Transaction',
    voucher: txn.voucher || txn.reference || txn.txnRef || txn.Voucher || txn.Reference || '',
  }));
};

const formatTransactionDateLabel = (dateValue, locale = 'en-IN') => {
  if (!dateValue || dateValue === '-') return '-';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return String(dateValue);
  }
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
};

/**
 * Generates a clean display name by prioritizing product branding.
 * @param {Object} account - The account object.
 * @returns {string} The formatted display name.
 */
const getAccountDisplayName = (account) => {
  const {
    loanProduct,    // e.g., 'Personal Loan'
    depositProduct, // e.g., 'Super Saver FD'
    acType,         // e.g., 'FD', 'SAVINGS'
    depositType,    // e.g., 'CUML', 'MIS'
    acno            // e.g., '01121135499'
  } = account;

  // 1. Identify the primary product label
  const productName = loanProduct || depositProduct || acType || 'Account';

  // 2. Map payout abbreviations
  const typeMap = {
    'CUML': 'Cumulative',
    'MIS': 'Monthly Int.',
    'QIS': 'Quarterly Int.',
    'HIS': 'Half-Yearly',
    'YIS': 'Yearly'
  };

  // 3. Format suffix: Add payout type if it exists for FD/RD
  const payoutInfo = typeMap[depositType] ? ` | ${typeMap[depositType]}` : '';
  const lastFour = acno ? ` (...${String(acno).slice(-4)})` : '';

  return `${productName}${payoutInfo}${lastFour}`;
};

// Sample response used for local development/testing
const SAMPLE_ACCOUNT_RESPONSE = {
  info: [
    { key: 'Acno', value: '03521003047', rank: 1 },
    { key: 'Ac Name', value: null, rank: 2 },
    { key: 'Loan Type', value: 'PERSONAL LOAN', rank: 2 },
    { key: 'Loan Date', value: '11-Dec-2024', rank: 3 },
    { key: 'Loan Amount', value: 800000, rank: 2 },
    { key: 'ROI', value: 8.0, rank: 2 },
    { key: 'Loan Tenure', value: '2/24', rank: 2 },
    { key: 'EMI Amount', value: 38667.0, rank: 2 },
    { key: 'Loan Prin Due', value: 355610.0, rank: 2 },
    { key: 'Overdue', value: 22286.0, rank: 2 },
    { key: 'Closing Amount', value: 357278.0, rank: 2 },
    { key: 'Close Interest', value: 1668, rank: 2 }
  ],
  trxn_history: [
    {
      recordId: 682367,
      creditAmount: 286.0,
      debitAmount: 0,
      balance: 0,
      ob: 0,
      transactionDate: '21-Feb-2026',
      remarks: 'REPAY',
      voucher: null,
    },
    {
      recordId: 682323,
      creditAmount: 2274.0,
      debitAmount: 0,
      balance: 0,
      ob: 0,
      transactionDate: '20-Feb-2026',
      remarks: 'REPAY',
      voucher: null,
    },
  ],
};

const buildAccountFromInfo = (infoArray) => {
  const accountInfo = {};
  if (!Array.isArray(infoArray)) return { detailRows: [] };
  infoArray.forEach((item) => {
    const key = String(item.key || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_');
    if (key) {
      accountInfo[key] = item.value;
      accountInfo[key.replace(/_/g, '')] = item.value;
    }
  });

  const pick = (...keys) => {
    for (const k of keys) {
      if (accountInfo[k] !== undefined && accountInfo[k] !== null && String(accountInfo[k]).trim() !== '') return accountInfo[k];
    }
    return undefined;
  };

  const fallbackBalance = toNumber(
    pick(
      'current_balance',
      'currentbalance',
      'available_balance',
      'availablebalance',
      'ledger_balance',
      'ledgerbalance',
      'account_balance',
      'accountbalance',
      'balance',
      'closing_amount',
      'closingamount',
      'loan_prin_due',
      'loanprindue',
      'outstanding_amount',
      'outstandingamount',
      "outstanding_principal",
      "loan_principal_amount"
    )
  );

  return {
    acno: pick('acno', 'account_no', 'accountno', 'a_c_no', 'ac_no', 'accno') || null,
    acType: pick('loan_type', 'loantype') || null,
    displayName: pick('loan_type', 'loantype', 'ac_name', 'acname') || 'Account',
    accountName: pick('ac_name', 'acname') || null,
    accountOpenDate: pick('loan_date', 'loandate') || null,
    balance: fallbackBalance || 0,
    detailRows: infoArray,
    emiAmount: toNumber(pick('emi_amount', 'emiamount')),
    loanTenure: pick('loan_tenure', 'loantenure'),
    roi: pick('roi', 'interestrate'),
    overdue: toNumber(pick('overdue')),
  };
};
export const AccountDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { needsCompactFont, locale, language, t, tenantBrand, tenantBrandEnglish } = useLocalization();
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementPeriod, setStatementPeriod] = useState('last_3months');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [loadedPeriodContext, setLoadedPeriodContext] = useState({
    period: 'last_3months',
    fromDate: '',
    toDate: '',
  });
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionsPerPage, setTransactionsPerPage] = useState(DEFAULT_TRANSACTIONS_PER_PAGE);
  const [showAmountInWords, setShowAmountInWords] = useState(() => {
    try {
      return localStorage.getItem('show_amount_words') === '1';
    } catch (_e) {
      return false;
    }
  });
  const accountContext = React.useMemo(() => {
    const stateContext = location.state?.accountContext;
    if (stateContext?.acType && stateContext?.acno) {
      const normalized = {
        acType: String(stateContext.acType).trim(),
        acno: String(stateContext.acno).trim(),
        dashboardTab: stateContext.dashboardTab === 'iowe' ? 'iowe' : 'iown',
      };
      saveSelectedAccountContext(normalized);
      return normalized;
    }
    return getSelectedAccountContext();
  }, [location.state]);

  const dashboardTab = accountContext?.dashboardTab === 'iowe' ? 'iowe' : 'iown';

  useEffect(() => {
    if (accountContext?.acType && accountContext?.acno) {
      return;
    }

    navigate(defaultAuthenticatedPath, {
      replace: true,
      state: {
        dashboardTab,
      },
    });
  }, [accountContext, dashboardTab, defaultAuthenticatedPath, navigate]);

  const fetchAccountDetails = useCallback(async () => {
    if (!accountContext?.acType || !accountContext?.acno) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const acType = String(accountContext?.acType || '').trim();
      const acno = String(accountContext?.acno || '').trim();

      if (!acType || !acno) {
        throw new Error(t('account.invalidAccount'));
      }

      const response = await accountsAPI.getAccountDetails(acno, acType);
      // Normalize response: some backends return `data.value` as a JSON string
      const raw = response?.data || {};
      let data = raw;
      if (raw?.value) {
        if (typeof raw.value === 'object') {
          data = raw.value;
        } else if (typeof raw.value === 'string') {
          try {
            data = JSON.parse(raw.value);
          } catch (e) {
            // fallback to raw.value if it's not JSON
            data = raw.value;
          }
        }
      }

      // Treat `data.info` as authoritative display fields: build a simple lookup
      const infoMap = {};
      if (Array.isArray(data.info)) {
        data.info.forEach((item) => {
          const rawKey = String(item.key || '').trim();
          if (!rawKey) return;
          const lower = rawKey.toLowerCase();
          const snake = lower.replace(/[^a-z0-9]+/g, '_');
          const nospace = lower.replace(/[^a-z0-9]+/g, '');
          const camel = snake.replace(/_([a-z0-9])/g, (_m, p1) => p1.toUpperCase());

          const value = item.value;
          infoMap[lower] = value;
          infoMap[snake] = value;
          infoMap[nospace] = value;
          infoMap[camel] = value;
          infoMap[rawKey] = value; // original key preserved
        });
      }

      const pickValue = (...keys) => {
        for (const key of keys) {
          const normalized = String(key || '').trim().toLowerCase();
          const value = infoMap[normalized];
          if (value !== undefined && value !== null && String(value).trim() !== '') return value;
        }
        return undefined;
      };

      const fallbackBalance = toNumber(
        data.current_balance ||
          data.currentBalance ||
          data.available_balance ||
          data.availableBalance ||
          data.ledger_balance ||
          data.ledgerBalance ||
          data.account_balance ||
          data.accountBalance ||
          data.balance ||
          data.closing_amount ||
          data.loan_prin_due ||
          data.outstanding_amount ||
          data.loan_closing_amount ||
          data.loanPrincipalAmount
      );
      const normalizedBalance = toNumber(
        pickValue(
          'current_balance',
          'currentbalance',
          'available_balance',
          'availablebalance',
          'ledger_balance',
          'ledgerbalance',
          'account_balance',
          'accountbalance',
          'balance',
          'closing_amount',
          'closingamount',
          'loan_prin_due',
          'loanprindue',
          'outstanding_amount',
          'outstandingamount',
          'loan_closing_amount',
          'loanclosingamount',
          'loan_principal_amount',
          'loanprincipalamount',
          'outstanding_principal',
          'outstandingprincipal'
        ) || fallbackBalance
      );
      const normalizedAccno =
        pickValue('acno', 'account_no', 'accountno', 'a_c_no', 'ac_no', 'accno') ||
        data.acno ||
        data.acNo ||
        acno;
      const normalizedAcType =
        data.acType ||
        data.accountType ||
        acType ||
        pickValue('ac_type', 'actype', 'a_c_type', 'account_type', 'accounttype') ||
        '';

      setAccount({
        acno: normalizedAccno,
        acType: normalizedAcType,
        displayName: getAccountDisplayName({
          loanProduct: data.loanProduct || pickValue('loan_product', 'loanproduct'),
          depositProduct: data.depositProduct || pickValue('deposit_product', 'depositproduct'),
          acType: normalizedAcType,
          depositType: data.depositType || pickValue('deposit_type', 'deposittype'),
          acno: normalizedAccno,
        }),
        balance: normalizedBalance,
        accountName:
          pickValue('ac_name', 'acname', 'a_c_name', 'account_name', 'accountname', 'name') ||
          data.accountName ||
          data.acName,
        accountOpenDate:
          pickValue('loan_date', 'loandate', 'opened_date', 'openeddate', 'open_date', 'opendate') ||
          data.accountOpenDate,
        accountMaturityDate:
          pickValue('maturity_date', 'maturitydate', 'account_maturity_date', 'accountmaturitydate') ||
          data.accountMaturityDate,
        recurringAmount: toNumber(pickValue('recurring_amount', 'recurringamount') || data.recurringAmount),
        agentName: pickValue('agent_name', 'agentname') || data.agentName,
        agentCode: pickValue('agent_code', 'agentcode') || data.agentCode,
        emiAmount: toNumber(pickValue('emi_amount', 'emiamount') || data.emiAmount),
        loanTenure: pickValue('loan_tenure', 'loantenure') || data.loanTenure,
        roi: pickValue('roi', 'interest_rate', 'interestrate') || data.roi,
        overdue: toNumber(pickValue('overdue', 'over_due') || data.overdue),
        detailRows: Array.isArray(data.info) ? data.info.slice().sort((a, b) => (Number(a.rank || 0) - Number(b.rank || 0))) : [],
        ...infoMap,
      });

      setTransactions(normalizeTransactions(data.trxn_history || data.trxn || data.trxnHistory || []));
    } catch (error) {
      const message = resolveApiErrorMessage(error, t, t('account.failedToLoad'));
      setErrorMessage(message);
      toast.error(message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [accountContext, t]);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  useEffect(() => {
    setTransactionPage(1);
  }, [transactions]);

  useEffect(() => {
    setTransactionPage(1);
  }, [transactionsPerPage]);

  useEffect(() => {
    try {
      localStorage.setItem('show_amount_words', showAmountInWords ? '1' : '0');
    } catch (_e) {
      // Ignore storage write errors.
    }
  }, [showAmountInWords]);

  const handleBackToDashboard = () => {
    navigate(defaultAuthenticatedPath, {
      state: {
        dashboardTab,
      },
    });
  };

  const handleViewFullStatement = async () => {
    if (statementPeriod === 'custom') {
      if (!customFromDate || !customToDate) {
        toast.error(t('account.selectCustomDates'));
        return;
      }
      if (customFromDate > customToDate) {
        toast.error(t('account.invalidDateRange'));
        return;
      }
    }

    setStatementLoading(true);
    try {
      const fromDate = statementPeriod === 'custom' ? customFromDate : '';
      const toDate = statementPeriod === 'custom' ? customToDate : '';

      const response = await accountsAPI.getStatement({
        acno: account.acno,
        acType: account.acType,
        format: 'list',
        chosenOption: statementPeriod,
        fromDate,
        toDate,
      });

      const statementPayload = Array.isArray(response?.data?.value)
        ? response.data.value
        : Array.isArray(response?.data)
          ? response.data
          : [];
      if (statementPayload.length > 0) {
        setTransactions(normalizeTransactions(statementPayload));
        setLoadedPeriodContext({
          period: statementPeriod,
          fromDate,
          toDate,
        });
        toast.success(t('account.statementLoaded'));
      } else {
        toast.info(t('account.noTransactionsForPeriod'));
      }
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('account.statementFailed')));
      console.error(error);
    } finally {
      setStatementLoading(false);
    }
  };

  const handleDownloadStatement = async () => {
    if (statementPeriod === 'custom') {
      if (!customFromDate || !customToDate) {
        toast.error(t('account.selectCustomDates'));
        return;
      }
      if (customFromDate > customToDate) {
        toast.error(t('account.invalidDateRange'));
        return;
      }
    }

    let targetTransactions = transactions;
    const fromDate = statementPeriod === 'custom' ? customFromDate : '';
    const toDate = statementPeriod === 'custom' ? customToDate : '';

    const needsFetch =
      loadedPeriodContext.period !== statementPeriod ||
      (statementPeriod === 'custom' &&
        (loadedPeriodContext.fromDate !== customFromDate || loadedPeriodContext.toDate !== customToDate));

    if (needsFetch) {
      setStatementLoading(true);
      toast.info(t('account.preparingStatement'));
      try {
        const response = await accountsAPI.getStatement({
          acno: account.acno,
          acType: account.acType,
          format: 'list',
          chosenOption: statementPeriod,
          fromDate,
          toDate,
        });

        const statementPayload = Array.isArray(response?.data?.value)
          ? response.data.value
          : Array.isArray(response?.data)
            ? response.data
            : [];
        
        targetTransactions = normalizeTransactions(statementPayload);
        setTransactions(targetTransactions);
        setLoadedPeriodContext({
          period: statementPeriod,
          fromDate,
          toDate,
        });
      } catch (error) {
        toast.error(resolveApiErrorMessage(error, t, t('account.statementFailed')));
        console.error(error);
        setStatementLoading(false);
        return;
      } finally {
        setStatementLoading(false);
      }
    }

    try {
      if (targetTransactions.length === 0) {
        toast.info(t('account.noTransactionsForPeriod'));
        return;
      }

      toast.info(t('account.preparingStatement'));
      setStatementLoading(true);

      const doc = await generateStatementPdf(
        account,
        targetTransactions,
        tenantBrandEnglish,
        statementPeriod,
        customFromDate,
        customToDate,
        locale,
        t
      );

      doc.save(`statement_${account.acno}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t('account.downloadSuccess'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(resolveApiErrorMessage(error, t, t('account.downloadFailed')));
    } finally {
      setStatementLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(toNumber(amount));
  };

  const formatAccountInfoValue = (key, value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const normalizedKey = String(key || '').trim().toLowerCase();
    const numericValue = String(value).replace(/,/g, '').trim();
    if (/\b(?:amount|balance|due|overdue|closing|outstanding|price|principal|penality)\b/i.test(normalizedKey) 
      && /^-?\d+(?:\.\d+)?$/.test(numericValue) ){
    return formatCurrency(Number(numericValue));
  }
    return String(value);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 dark:border-slate-700 border-t-primary-500 dark:border-t-primary-400"></div>
            <p className="text-primary-700 dark:text-primary-300">{t('account.loadingDetails')}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!account) {
    return (
      <DashboardLayout>
        <div className="app-surface p-12 text-center">
          <p className="text-primary-700 dark:text-primary-300 mb-6">{errorMessage || t('account.accountNotFound')}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={fetchAccountDetails}
              className="app-primary-button"
              data-testid="retry-account-details-button"
            >
              {t('common.retry')}
            </button>
            <button
              onClick={handleBackToDashboard}
              className="app-secondary-button"
            >
              {t('common.returnToDashboard')}
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const accountType = String(account.acType || '').toUpperCase();
  const isLiability =
    account.balance < 0 ||
    accountType.includes('LOAN') ||
    accountType.includes('MORTGAGE') ||
    accountType.includes('CC');
  const totalTransactionPages = Math.max(1, Math.ceil(transactions.length / transactionsPerPage));
  const currentTransactionPage = Math.min(transactionPage, totalTransactionPages);
  const pageStartIndex = (currentTransactionPage - 1) * transactionsPerPage;
  const paginatedTransactions = transactions.slice(pageStartIndex, pageStartIndex + transactionsPerPage);
  const visibleFrom = transactions.length === 0 ? 0 : pageStartIndex + 1;
  const visibleTo = Math.min(pageStartIndex + transactionsPerPage, transactions.length);
  const accountInfoRows = Array.isArray(account.detailRows)
    ? account.detailRows.filter((row) => !isCurrentBalanceDetailRow(row))
    : [];

  return (
    <DashboardLayout>
      <div className="app-page-shell max-w-5xl space-y-4 sm:space-y-5 fade-in" data-testid="account-details-page">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4 pb-1 sm:pb-2">
          <button
            onClick={handleBackToDashboard}
            className="app-secondary-button p-2 sm:px-3 sm:py-2 text-primary-700 dark:text-primary-300 flex-shrink-0"
            data-testid="back-to-dashboard-button"
            title={t('account.backToDashboard')}
          >
            <ArrowLeft size={24} className="sm:w-6 sm:h-6" />
          </button>
          <div className="min-w-0">
            <h1 className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}>
              {account.displayName || account.acType}
            </h1>
          </div>
        </div>

        {/* Key Metrics - Balance Card Only */}
        <div className={`rounded-2xl p-4 sm:p-5 text-white shadow-lg ${
          isLiability 
            ? 'bg-gradient-to-br from-red-500 to-red-600' 
            : 'bg-primary-500'
        }`} data-testid="account-balance-card">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className={`text-xs sm:text-sm font-medium ${isLiability ? 'text-red-100' : 'text-primary-100'}`}>
              {isLiability ? t('account.balanceLabelLiability') : t('account.balanceLabelAsset')}
            </p>
            <button
              type="button"
              onClick={() => setShowAmountInWords((prev) => !prev)}
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                showAmountInWords
                  ? 'bg-white/25 border-white/40 text-white'
                  : 'bg-white/10 border-white/30 text-white/90 hover:bg-white/20'
              }`}
              data-testid="toggle-account-amount-words"
              title={t('dashboard.toggleAmountWords')}
            >
              {showAmountInWords ? t('common.wordsOn') : t('common.words')}
            </button>
          </div>
          <p className="font-serif text-3xl sm:text-4xl font-bold mb-1">
            {formatCurrency(Math.abs(account.balance))}
          </p>
          {showAmountInWords && (
            <p className={`text-xs sm:text-sm leading-relaxed font-sans font-medium ${isLiability ? 'text-red-100' : 'text-primary-100'}`}>
              {formatIndianCurrencyWords(Math.abs(account.balance), language)}
            </p>
          )}
        </div>

        {/* Account Details Table */}
        {accountInfoRows.length > 0 && (
          <div className="app-surface overflow-hidden" data-testid="account-info-section">
            <div className="p-4 sm:p-5 border-b border-primary-200 dark:border-slate-800">
              <h2 className="app-panel-title">{t('common.accountInformation')}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody>
                  {accountInfoRows.map((row, index) => (
                    <tr
                      key={`${row.key || 'row'}-${index}`}
                      className={`border-b border-primary-200 dark:border-slate-800 hover:bg-primary-50 dark:hover:bg-slate-800/50 transition-colors ${
                        index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-primary-50 dark:bg-slate-800/30'
                      }`}
                    >
                      <td className="px-4 sm:px-6 py-2.5 sm:py-3.5 text-sm font-medium text-primary-700 dark:text-primary-300 w-1/2">
                        {row.key || '-'}
                      </td>
                      <td className="px-4 sm:px-6 py-2.5 sm:py-3.5 text-right">
                        <span className="text-primary-900 dark:text-white font-semibold text-sm">
                          {formatAccountInfoValue(row.key, row.value)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transactions Section */}
        <div className="app-surface overflow-hidden">
          {/* Header */}
          <div className="p-4 sm:p-5 border-b border-primary-200 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
              <div>
                <h2 className="app-panel-title mb-1">
                  {t('common.transactions')}
                </h2>
                <p className="text-sm text-primary-700 dark:text-primary-300">
                  {t('account.showingTransactions', {
                    from: visibleFrom,
                    to: visibleTo,
                    total: transactions.length,
                    suffix: transactions.length !== 1 ? 's' : '',
                  })}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                <select
                  value={statementPeriod}
                  onChange={(e) => {
                    const nextPeriod = e.target.value;
                    setStatementPeriod(nextPeriod);
                    if (nextPeriod !== 'custom') {
                      setCustomFromDate('');
                      setCustomToDate('');
                    }
                  }}
                  className="px-4 py-2 text-sm border border-primary-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-primary-900 dark:text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  data-testid="statement-period-select"
                >
                  <option value="last_3months">{t('account.last3Months')}</option>
                  <option value="last_6months">{t('account.last6Months')}</option>
                  <option value="current_fy">{t('account.currentFy')}</option>
                  <option value="previous_fy">{t('account.previousFy')}</option>
                  <option value="custom">{t('account.custom')}</option>
                </select>
                {statementPeriod === 'custom' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                        {t('common.from')}
                      </span>
                      <input
                        type="date"
                        value={customFromDate}
                        max={customToDate || undefined}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                        className="px-3 py-2 text-sm border border-primary-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-primary-900 dark:text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        data-testid="custom-from-date-input"
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
                        {t('common.to')}
                      </span>
                      <input
                        type="date"
                        value={customToDate}
                        min={customFromDate || undefined}
                        onChange={(e) => setCustomToDate(e.target.value)}
                        className="px-3 py-2 text-sm border border-primary-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-primary-900 dark:text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                        data-testid="custom-to-date-input"
                      />
                    </label>
                  </div>
                )}
                <button
                  onClick={handleViewFullStatement}
                  disabled={statementLoading}
                  data-testid="view-full-statement-button"
                  className="app-primary-button whitespace-nowrap px-4 sm:px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {statementLoading ? t('common.loading') : t('account.viewStatement')}
                </button>
                <button
                  onClick={handleDownloadStatement}
                  data-testid="download-statement-button"
                  className="app-primary-button whitespace-nowrap px-4 sm:px-6 py-2"
                >
                  <Download size={18} />
                  <span>{t('account.downloadPdf')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Transactions List */}
          {transactions.length === 0 ? (
            <div className="text-center py-12 sm:py-16 px-6 sm:px-8" data-testid="no-transactions-message">
              <Calendar className="w-14 sm:w-16 h-14 sm:h-16 mx-auto mb-4 text-primary-300 dark:text-primary-700" strokeWidth={1.5} />
              <p className="text-primary-700 dark:text-primary-300 text-lg">{t('common.noTransactionsFound')}</p>
            </div>
          ) : (
            <div className="overflow-x-hidden sm:overflow-x-auto" data-testid="transactions-list">
              <div className="sm:hidden divide-y divide-primary-200 dark:divide-slate-800">
                {paginatedTransactions.map((txn, index) => {
                  const creditAmount = toNumber(txn.creditAmount);
                  const debitAmount = toNumber(txn.debitAmount);
                  const isCredit = creditAmount >= debitAmount;
                  const amount = isCredit ? creditAmount : debitAmount;

                  return (
                    <div
                      key={txn.recordId || index}
                      className="px-4 py-4 bg-white dark:bg-slate-900"
                      data-testid={`transaction-mobile-${txn.recordId || index}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-primary-600 dark:text-primary-300">
                            {formatTransactionDateLabel(txn.transactionDate, locale)}
                          </p>
                          <p className="mt-1.5 text-sm font-semibold leading-5 text-primary-900 dark:text-white break-words">
                            {txn.remarks || t('account.transaction')}
                          </p>
                          {txn.voucher && (
                            <p className="mt-1.5 text-xs leading-5 text-primary-600 dark:text-primary-300 break-all">
                              {t('account.ref')}: {txn.voucher}
                            </p>
                          )}
                        </div>

                        <div className="shrink-0 text-right">
                          <p
                            className={`text-sm font-bold tabular-nums whitespace-nowrap ${
                              isCredit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            {isCredit ? '+' : '-'} {formatCurrency(amount)}
                          </p>
                          <span
                            className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${
                              isCredit
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}
                          >
                            {isCredit ? 'CR' : 'DR'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50/90 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-800/60">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-medium text-primary-600 dark:text-primary-300">
                            {t('common.balance')}
                          </span>
                          <span className="text-sm font-semibold tabular-nums text-primary-900 dark:text-white whitespace-nowrap">
                            {txn.balance !== undefined && txn.balance !== null
                              ? formatCurrency(txn.balance)
                              : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <table className="hidden sm:table w-full text-sm table-auto">
                <thead>
                  <tr className="bg-primary-50 dark:bg-slate-800/50 border-b border-primary-200 dark:border-slate-800">
                    <th className="w-[52%] sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 text-left text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">{t('account.transaction')}</th>
                    <th className="hidden sm:table-cell px-4 sm:px-6 py-2.5 sm:py-3 text-left text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider whitespace-nowrap">{t('common.date')}</th>
                    <th className="px-4 sm:px-6 py-2.5 sm:py-3 text-right text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">{t('common.amount')}</th>
                    <th className="hidden md:table-cell px-4 sm:px-6 py-2.5 sm:py-3 text-right text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wider">{t('common.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((txn, index) => {
                    const creditAmount = toNumber(txn.creditAmount);
                    const debitAmount = toNumber(txn.debitAmount);
                    const isCredit = creditAmount >= debitAmount;
                    const amount = isCredit ? creditAmount : debitAmount;
                    const absoluteIndex = pageStartIndex + index;
                    
                    return (
                      <tr
                        key={txn.recordId || index}
                        className={`border-b border-primary-200 dark:border-slate-800 hover:bg-primary-50 dark:hover:bg-slate-800/50 transition-colors ${
                          absoluteIndex % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-primary-50 dark:bg-slate-800/30'
                        }`}
                        data-testid={`transaction-${txn.recordId || index}`}
                      >
                        <td className="w-[52%] sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                              isCredit 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                                : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            }`}>
                              {isCredit ? (
                                <ArrowDownRight size={16} className="sm:w-[18px] sm:h-[18px]" strokeWidth={2} />
                              ) : (
                                <ArrowUpRight size={16} className="sm:w-[18px] sm:h-[18px]" strokeWidth={2} />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-primary-900 dark:text-white text-sm leading-normal break-words sm:truncate">
                                {txn.remarks || t('account.transaction')}
                              </p>
                              {txn.voucher && (
                                <p className="text-xs text-primary-500 dark:text-primary-500 mt-1 truncate hidden sm:block">
                                  {t('account.ref')}: {txn.voucher}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-primary-700 dark:text-primary-300 font-medium mt-1 sm:hidden">
                            {txn.transactionDate}
                          </p>
                        </td>
                        <td className="hidden sm:table-cell px-4 sm:px-6 py-2.5 sm:py-3.5 whitespace-nowrap">
                          <span className="text-primary-700 dark:text-primary-300 font-medium text-sm">
                            {txn.transactionDate}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-2.5 sm:py-3.5 text-right">
                          <span className={`font-bold text-sm sm:text-lg ${
                            isCredit 
                              ? 'text-green-600 dark:text-green-400' 
                              : 'text-red-600 dark:text-red-400'
                          }`}>
                            {isCredit ? '+' : '-'} {formatCurrency(amount)}
                          </span>
                          <p className="md:hidden text-xs text-primary-700 dark:text-primary-300 mt-1">
                            {t('common.balance')}:{' '}
                            {txn.balance !== undefined && txn.balance !== null
                              ? formatCurrency(txn.balance)
                              : '-'}
                          </p>
                        </td>
                        <td className="hidden md:table-cell px-4 sm:px-6 py-2.5 sm:py-3.5 text-right">
                          {txn.balance !== undefined && txn.balance !== null ? (
                            <span className="text-primary-900 dark:text-white font-semibold text-sm">
                              {formatCurrency(txn.balance)}
                            </span>
                          ) : (
                            <span className="text-primary-500 dark:text-primary-500">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {transactions.length > transactionsPerPage && (
            <div className="flex items-center justify-between gap-2 px-3 sm:px-6 py-3 border-t border-primary-200 dark:border-slate-800">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <p className="text-xs sm:text-sm text-primary-700 dark:text-primary-300 whitespace-nowrap">
                  <span className="sm:hidden">{t('common.pageShort', { current: currentTransactionPage, total: totalTransactionPages })}</span>
                  <span className="hidden sm:inline">{t('common.pageFull', { current: currentTransactionPage, total: totalTransactionPages })}</span>
                </p>
                <label className="flex items-center gap-1.5 text-xs sm:text-sm text-primary-700 dark:text-primary-300 whitespace-nowrap">
                  <span>{t('common.rows')}</span>
                  <select
                    value={transactionsPerPage}
                    onChange={(e) => setTransactionsPerPage(Number(e.target.value) || DEFAULT_TRANSACTIONS_PER_PAGE)}
                    className="h-8 px-2 rounded-md border border-primary-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-primary-900 dark:text-white text-xs sm:text-sm"
                    data-testid="transactions-page-size-select"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setTransactionPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentTransactionPage === 1}
                  className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-primary-200 dark:border-slate-700 text-primary-800 dark:text-primary-200 hover:bg-primary-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="transactions-pagination-prev"
                >
                  <span className="sm:hidden">{t('common.previous')}</span>
                  <span className="hidden sm:inline">{t('common.previous')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTransactionPage((prev) => Math.min(totalTransactionPages, prev + 1))}
                  disabled={currentTransactionPage === totalTransactionPages}
                  className="px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-primary-200 dark:border-slate-700 text-primary-800 dark:text-primary-200 hover:bg-primary-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="transactions-pagination-next"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
